/**
 * Structured Data Schemas (JSON-LD) para SEO
 * Documentação: https://schema.org
 */

const baseUrl = import.meta.env.VITE_PUBLIC_APP_URL || 'https://magistralhomeopatia.com.br';

/**
 * Schema para MedicalWebPage
 * Usado na página principal e páginas relacionadas à saúde
 */
export const medicalWebPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalWebPage',
  'name': 'Magistral Homeopatia - Gestão de Tratamentos Homeopáticos',
  'description': 'Primeiro aplicativo completo para pacientes de homeopatia no Brasil. Gestão inteligente de tratamentos familiares e individuais com lembretes, biblioteca de remédios, consultas com homeopata virtual e gamificação.',
  'url': baseUrl,
  'image': `${baseUrl}/og-image.png`,
  'publisher': {
    '@type': 'Organization',
    'name': 'Magistral Homeopatia',
    'logo': {
      '@type': 'ImageObject',
      'url': `${baseUrl}/icons/icon-512x512-v2.png`
    }
  },
  'medicalAudience': [
    {
      '@type': 'MedicalAudience',
      'audienceType': 'Patient',
      'healthCondition': {
        '@type': 'MedicalCondition',
        'name': 'General Health'
      }
    }
  ],
  'about': {
    '@type': 'MedicalBusiness',
    'name': 'Magistral Homeopatia',
    'alternateName': 'Magistral App',
    'description': 'Plataforma digital para gestão de tratamentos homeopáticos'
  }
};

/**
 * Schema para SoftwareApplication
 * Usado para descrever o aplicativo
 */
export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  'name': 'Magistral Homeopatia',
  'applicationCategory': 'HealthApplication',
  'operatingSystem': 'Web, iOS, Android',
  'offers': {
    '@type': 'Offer',
    'price': '0',
    'priceCurrency': 'BRL',
    'availability': 'https://schema.org/InStock'
  },
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '4.8',
    'ratingCount': '150',
    'bestRating': '5',
    'worstRating': '1'
  },
  'description': 'Primeiro aplicativo completo para pacientes de homeopatia no Brasil. Gestão inteligente de tratamentos familiares e individuais com lembretes, biblioteca de remédios, consultas com homeopata virtual e gamificação.',
  'screenshot': `${baseUrl}/screenshots/narrow-1080x1920.png`,
  'featureList': [
    'Gestão de medicamentos homeopáticos',
    'Lembretes inteligentes de doses',
    'Biblioteca de 6+ remédios',
    'Consultas SOS com homeopata virtual',
    'Sistema de gamificação',
    'Quiz educativo diário',
    'Gestão familiar (até 10 membros)'
  ],
  'author': {
    '@type': 'Organization',
    'name': 'Magistral Homeopatia'
  },
  'datePublished': '2025-01-01',
  'inLanguage': 'pt-BR'
};

/**
 * Schema para Organization
 * Usado para informações da empresa/organização
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'Magistral Homeopatia',
  'url': baseUrl,
  'logo': `${baseUrl}/icons/icon-512x512-v2.png`,
  'description': 'Plataforma digital para gestão de tratamentos homeopáticos familiares e individuais',
  'contactPoint': {
    '@type': 'ContactPoint',
    'contactType': 'Customer Support',
    'availableLanguage': ['Portuguese']
  }
};

/**
 * Schema para FAQPage
 * Usado na página principal ou página de FAQ
 */
export const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': [
    {
      '@type': 'Question',
      'name': 'O que é o Magistral Homeopatia?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Magistral Homeopatia é o primeiro aplicativo completo para pacientes de homeopatia no Brasil. Oferece gestão inteligente de tratamentos familiares e individuais com lembretes inteligentes, biblioteca de remédios, consultas com homeopata virtual e sistema de gamificação para aumentar a adesão ao tratamento.'
      }
    },
    {
      '@type': 'Question',
      'name': 'O Magistral Homeopatia é gratuito?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Sim! O Magistral Homeopatia oferece um plano totalmente gratuito com acesso a funcionalidades essenciais. Também disponibilizamos planos Básico e Premium com recursos avançados para famílias que precisam de mais funcionalidades.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Quantos membros da família posso adicionar?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'No plano Básico gratuito você pode adicionar até 3 membros da família. No plano Premium você pode gerenciar até 10 membros, ideal para famílias maiores.'
      }
    },
    {
      '@type': 'Question',
      'name': 'O que são consultas SOS?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Consultas SOS são atendimentos emergenciais com homeopata virtual especializado. Você recebe orientações rápidas sobre sintomas e possíveis remédios homeopáticos para situações urgentes.'
      }
    },
    {
      '@type': 'Question',
      'name': 'O aplicativo substitui consultas médicas?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Não. O Magistral Homeopatia é uma ferramenta de apoio à gestão de tratamentos homeopáticos. Sempre consulte um médico ou homeopata qualificado antes de iniciar qualquer tratamento. O app não fornece diagnósticos médicos.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Como funciona o sistema de lembretes?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'O Magistral Homeopatia envia notificações push inteligentes nos horários das suas medicações. Você pode configurar múltiplos horários, pausar temporariamente e acompanhar seu histórico de adesão ao tratamento.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Posso usar o Magistral offline?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'O Magistral é um PWA (Progressive Web App) que funciona parcialmente offline. Você pode consultar a biblioteca de remédios e registrar doses mesmo sem conexão. Os dados são sincronizados quando a conexão é restabelecida.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Meus dados são seguros?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Sim! O Magistral é totalmente compatível com a LGPD. Seus dados são criptografados, armazenados com segurança e nunca são compartilhados com terceiros sem seu consentimento explícito.'
      }
    }
  ]
};

/**
 * Schema para BreadcrumbList
 * Usado em páginas internas para navegação
 */
export const createBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': `${baseUrl}${item.url}`
  }))
});

/**
 * Schema para WebPage genérico
 */
export const createWebPageSchema = (name: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  'name': name,
  'description': description,
  'url': `${baseUrl}${url}`,
  'inLanguage': 'pt-BR',
  'isPartOf': {
    '@type': 'WebSite',
    'name': 'Magistral',
    'url': baseUrl
  }
});
